import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {ProgressActionsServiseService} from '../progress-actions-servise.service';
import {EventEventType, EventResolutionStatus, OfferPropositionInterested} from '../../../../generated/graphql';
import {LivasStatuses} from '../../../livas/livas.service';
import * as moment from 'moment';
import {ProgressBarData, ProgressStages} from '../progress-component/types';
import {CancellationServiceService} from '../../cancellation-reason-dialog/cancellation-service.service';
import { OrderTypeEnum } from 'src/app/shared/types/order-types';
import {RelayIdService} from '../../../shared/relay-id.service';
import { NotificationService } from '../../../shared/notification.service';
import { FormControl } from '@angular/forms';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { debounceTime, tap } from 'rxjs/operators';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'YYYY MMMM',
    dateA11yLabel: 'YYYY-MM-DD',
    monthYearA11yLabel: 'YYYY MMMM',
  },
};

@Component({
  selector: 'app-progress-action-dialog',
  templateUrl: './progress-action-dialog.component.html',
  styleUrls: ['./progress-action-dialog.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }]
})
export class ProgressActionDialogComponent implements OnInit {
  loading = false;
  countryIsoId: string = 'lt';
  stages = ProgressStages;
  resolutions = EventResolutionStatus;
  livasResolutions = LivasStatuses;
  interested = OfferPropositionInterested;

  eventId: string;
  dateControl = new FormControl(moment());
  hourControl = new FormControl('10:00');
  isContinueDisabled = false;
  eventTypes = EventEventType;

  showNextContactDate = false;
  extraDate = moment().add(moment().day() === 5 ? 3 : 1, 'day');
  selectedCancellationAnswer: OfferPropositionInterested;
  isProblematicInterview = false;
  problematicInterviewDescription = '';
  problematicLoading = false;

  selectedLivasStatus: any;
  selectedCancellationReasons: string[] = [];
  livasStatusOptions = [
    { label: 'LIVAS_STATUSES.WILL_NOT_OFFER', id: LivasStatuses.wontOffer },
    { label: 'LIVAS_STATUSES.CURRENLTY_NOT_ACTIVE', id: LivasStatuses.currentlyNotActive },
    { label: 'LIVAS_STATUSES.CANDIDATE_WAITS_OFFER', id: LivasStatuses.candidateWaitsOffer },
    { label: 'LIVAS_STATUSES.CANNOT_CONTACT', id: LivasStatuses.cannotContact },
    { label: 'LIVAS_STATUSES.WAIT_FOR_DECISION', id: LivasStatuses.waitingDecision },
    { label: 'LIVAS_STATUSES.REDIRECTED_TO_WORKIS', id: LivasStatuses.redirectedToWorkis },
  ];
  doResolvePreviousEvents = true;
  OrderTypeEnum = OrderTypeEnum;
  selectedNegativeResolutionStatus: EventResolutionStatus;

  selectedInterviewNoShow = false;
  selectedResolutionStatus;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      events: ProgressBarData,
      stage: ProgressStages,
      personId: number,
      countryIsoId: string,
    },
    public progressService: ProgressActionsServiseService,
    private dialog: MatDialogRef<ProgressActionDialogComponent>,
    private cancellationService: CancellationServiceService,
    private translateService: TranslateService,
    private relayService: RelayIdService,
    private notify: NotificationService
  ) {
    const event = data.events[data.stage];
    this.eventId = event?.id;
    this.countryIsoId = this.data.countryIsoId;
    if (event?.startDatetime) {
      this.dateControl.setValue(moment(event.startDatetime));
      this.hourControl.setValue(moment(this.dateControl.value).format('HH:mm'));
    }
  }

  ngOnInit(): void {
    if (this.data.stage === this.stages.officeInterviewInvitationEvent) {
      this.isProblematicInterview = this.data.events['officeInterview']?.isProblematicInterview || false;
      this.problematicInterviewDescription = this.data.events['officeInterview']?.description || '';
    }

    this.hourControl.valueChanges.pipe(tap(v =>
      this.isContinueDisabled = true
    ), debounceTime(1000)).subscribe((value: string) => {
      if (!value) {
        this.hourControl?.setValue('10:00', { emitEvent: false });
        this.isContinueDisabled = false;
        return;
      }

      const control = this.hourControl;

      const [rawH = '', rawM = ''] = value.split(':');

      let h = parseInt(rawH, 10);
      let m = parseInt(rawM, 10);

      if (isNaN(h)) {
        return;
      }

      h = Math.min(Math.max(h, 0), 23);
      m = isNaN(m) ? 0 : Math.min(Math.max(m, 0), 59);

      const formatted =
        `${h.toString().padStart(2, '0')}:` +
        `${m.toString().padStart(2, '0')}`;

      if (formatted !== value) {
        control?.setValue(formatted, { emitEvent: false });
      }
      this.isContinueDisabled = false;
    });

    this.dateControl.valueChanges.pipe(tap(v => {
      this.isContinueDisabled = true;
    }), debounceTime(1500)).subscribe(value => {
      if (!value) {
        this.dateControl.setErrors({invalid: true});
        this.isContinueDisabled = false;
        return;
      }
      this.dateControl.setValue(value, { emitEvent: false });
      this.isContinueDisabled = false;
    });
  }

  isBtnDisable() {
    return this.loading || !this.eventId;
  }

  private getDatetime() {
    return moment(this.dateControl.value.format('YYYY-MM-DD') + ' ' + this.hourControl.value).toISOString();
  }

  eventUpdate(resolution: EventResolutionStatus, eventType, autoclose= true, updateOfferSigned = false) {
    this.loading = true;
    const startDatetime = this.getDatetime();

    const livasStatusId = this.getLivasStatus(this.data.stage, resolution);
    const updateLivasOfficeInterviewDate = (eventType === EventEventType.Interview);
    const updateLivasClientInterviewDate = (eventType === EventEventType.ClientInterview);
    const updateLivasEmploymentDate = (eventType === EventEventType.Employment);
    const nextContactDate = this.getNextContactDate(eventType, startDatetime);
    if (this.doResolvePreviousEvents) {
      this.resolvePreviousEvents(this.data.stage, false);
    }

    if (!resolution && this.eventId) {
      // if resolution is null, we need to update status of event as unresolved
      this.eventStatusUpdate(this.eventId, resolution, false);
    }

    this.progressService.createUpdateEvent(
      this.data.events[this.data.stage].id,
      this.data.events.responseId,
      eventType,
      startDatetime,
      this.doResolvePreviousEvents ? livasStatusId : null,
      nextContactDate,
      updateLivasOfficeInterviewDate,
      updateLivasClientInterviewDate,
      updateLivasEmploymentDate
    ).subscribe(() => {
      if (nextContactDate || startDatetime) {
        window.parent.postMessage({
          contactDate: moment(nextContactDate || startDatetime).format('YYYY-MM-DD')
        },
          '*'
        );
      }

      if (autoclose) {
        this.dialog.close();
      }
    });
    if (updateOfferSigned) {
      this.progressService.setOfferPropositionSigned(this.data.events.responseId).subscribe();
    }

  }

  eventStatusUpdate(eventId, resolution, autoclose = true) {
    this.loading = true;
    this.progressService.changeEventStatus(
      eventId, resolution
    ).subscribe(next => {
      if (autoclose) {
          this.dialog.close();
      }
    });
  }

  employmentEventStatusUpdate(eventId, resolution, livasStatus, autoclose = true) {
    if (livasStatus) {
      this.progressService.updateLivasCandidateStatusByOfferProposition(
        livasStatus, btoa(`OfferProposition:${this.data.events.responseId}`)
      ).subscribe(() => {
        if (autoclose) {
          this.dialog.close();
        }
      });
    }
  }


  hasEventId(stage?: ProgressStages) {
    return this.eventId;
  }

  setInterviewInterest(interested) {
    this.progressService.updateCreateOfferProposition(
      this.data.events.responseId ? btoa('OfferPropositionType:' + this.data.events.responseId) : undefined,
      this.data.events.order,
      this.data.personId,
      interested
    ).subscribe(resp => {

      let offerId = this.data.events.responseId;

      if (! offerId) {
        const idStr = resp.data.offerPropositionCreate?.offerProposition?.id;
        offerId = idStr ? atob(idStr).split(':')[1] : null;
      }

      if (this.selectedCancellationReasons.length) {
        this.updateLivasStatusAndCancellationReasons(offerId);
      }

      if ((interested === this.interested.Interested) || this.selectedLivasStatus) {
        const newStatusId = interested === this.interested.Interested ? LivasStatuses.waitingDecision : this.selectedLivasStatus;
        if (newStatusId) {
          this.cancellationService.updateLivasStatus(btoa(`OfferProposition:${offerId}`), newStatusId).subscribe();
        }
      }

      const officeInterviewEvent = this.data.events['officeInterviewEvent'];
      if (officeInterviewEvent.id && (officeInterviewEvent.status !== 'SUCCESS')) {
        this.eventStatusUpdate(this.data.events['officeInterviewInvitationEvent'].id, this.resolutions.Complete);
      }

      this.dialog.close();
      }, err => {});
  }

  private getLivasStatus(stage: ProgressStages, resolution: EventResolutionStatus) {

    const stagesMap = {
      [ProgressStages.presentToClientEvent]: LivasStatuses.introducingToClient,
      [ProgressStages.trialDay]: LivasStatuses.trialDay,
      [ProgressStages.clientInterviewEvent]: LivasStatuses.clientInterview,
      [ProgressStages.healthcheckEvent]: (resolution === EventResolutionStatus.Complete) ? LivasStatuses.healthCheckCompleted :  LivasStatuses.healthCheckInProgress,
      [ProgressStages.employmentEvent]: LivasStatuses.healthCheckCompleted // if we decided to employ person, his currect status is "health check completed and waiting for employment
    };

    if (stage in stagesMap) {
      return stagesMap[stage];
    } else {
      return;
    }
  }

  checkNoshow(eventId: string) {
    this.showNextContactDate = true;

  }

  updateLivasNextContactDate(eventId, eventType) {
    this.updateLivasStatusAndCancellationReasons(this.data.events.responseId);
    this.progressService.changeEventStatus(eventId, this.resolutions.Failed).subscribe(
      re => {
        this.progressService.createUpdateEvent(
          eventId,
          this.data.events.responseId,
          eventType,
          undefined,
          undefined,
          moment(this.extraDate).format('YYYY-MM-DD')
        ).subscribe(re => this.dialog.close());
      }
    );

  }

  private getNextContactDate(eventType, startDatetime: string) {
    if ([EventEventType.ToBePresented, EventEventType.HealthCheckPending].indexOf(eventType) > -1) {
      return startDatetime.substr(0, 10);
    } else {
      return ;
    }
  }

  private resolvePreviousEvents(stage, autoclose) {
    let stagesToComplete;

    switch (stage) {
      case ProgressStages.employmentEvent: {
        stagesToComplete = [ProgressStages.healthcheckEvent, ProgressStages.clientInterviewEvent, ProgressStages.presentToClientEvent]
        break;
      }
      case ProgressStages.healthcheckEvent: {
        stagesToComplete = [ProgressStages.clientInterviewEvent, ProgressStages.presentToClientEvent];
        break;
      }
      case ProgressStages.clientInterviewEvent: {
        stagesToComplete = [ProgressStages.presentToClientEvent];
        break;
      }
      default: {
        stagesToComplete = [];
      }
    }
    stagesToComplete.map( stageName => {
      if (this.data.events[stageName]?.status === 'PENDING' ) {
        this.eventStatusUpdate( this.data.events[stageName].id, EventResolutionStatus.Complete, autoclose );
      }
    });

  }

  private updateLivasStatusAndCancellationReasons(offerId) {
    this.cancellationService
      .saveAssignments(offerId, this.selectedCancellationReasons)
      .subscribe();
    const globalId = this.relayService.getRelayId(offerId, 'OfferProposition')
    if (this.selectedLivasStatus) {
      this.cancellationService.updateLivasStatus(globalId, this.selectedLivasStatus).subscribe();
    }
  }

  onCancelationReasonsChange(ids: string[]) {
    this.selectedCancellationReasons = ids;
  }

  setEventNegativeOutcome(eventId, resolution, selectedCancellationAnswer: OfferPropositionInterested) {
    this.updateLivasStatusAndCancellationReasons(this.data.events.responseId);
    this.eventStatusUpdate(this.data.events[this.data.stage].id, resolution, true);
  }

  getStepStatus(step: string) {
    return this.data.events[step]?.resolutionStatus;
  }

  onProblematicInterviewChange() {
    this.problematicLoading = true;

    if (!this.isProblematicInterview) {
      this.problematicInterviewDescription = '';
    }

    this.progressService.problematicInterviewUpdate(
      this.data.events[this.data.stage].id,
      this.problematicInterviewDescription,
      this.isProblematicInterview
    ).subscribe(() => {
      this.notify.notify(this.translateService.instant('UPDATED_SUCCESSFULLY'));
      this.problematicLoading = false;
    });
  }

  protected readonly orderTypes = OrderTypeEnum;
}
